import WatchableValue from "../models/WatchableValue";
import type Connection from "../models/Connection";
import type BaseObject from "../models/BaseObject";
import type Consumer from "../models/Consumer";
import { collectSelectedConnections } from "./collectSelectedConnections";
import { connectionSelectionRole } from "./connectionSelectionPolicy";
import type { ConnectionEndpoint, ConnectionEndpointProvider, ConnectableEndpoint } from "./ConnectionEndpointProvider";
import { endpointContoursEqual } from "./endpointContour";
import { isVirtualConnection, type AnalyserConnection } from "./VirtualConnections";
import { isConnectionEndpointVisibleInMode, isConnectionVisibleInMode } from "./connectionAnalyserVisibility";

/** Transient registry of selected connections and control-owned viewport anchors. */
export class ConnectionAnalyser {
  readonly manuallyEnabled = new WatchableValue(false, "connectionAnalyser.manuallyEnabled");
  readonly portalTarget = new WatchableValue<HTMLElement | undefined>(undefined, "connectionAnalyser.portalTarget");
  readonly selectedVirtualConnectionId = new WatchableValue<string | undefined>(undefined, "connectionAnalyser.selectedVirtualConnectionId");

  /** Resolves selection against the latest derived graph rather than retaining stale assignments. */
  get selectedVirtualConnection() {
    const connection = this.current.find(item => item.id === this.selectedVirtualConnectionId.value);
    return connection && isVirtualConnection(connection) ? connection : undefined;
  }
  readonly visible = new WatchableValue(false, "connectionAnalyser.visible");
  readonly navigationActive = new WatchableValue(false, "connectionAnalyser.navigationActive");
  readonly layoutResizing = new WatchableValue(false, "connectionAnalyser.layoutResizing");
  readonly scrollingProviderIds = new WatchableValue<ReadonlySet<string>>(new Set(), "connectionAnalyser.scrollingProviderIds");
  readonly hasConnectionSources = new WatchableValue(false, "connectionAnalyser.hasConnectionSources");
  readonly selectedSource = new WatchableValue<BaseObject | undefined>(undefined, "connectionAnalyser.selectedSource");
  readonly endpoints = new WatchableValue<readonly ConnectionEndpoint[]>([], "connectionAnalyser.endpoints");
  private current: readonly AnalyserConnection[] = [];
  private dashboardUpdatesDeferred = false;
  private pendingScrollRelease = false;
  private finishingDashboardScroll = false;

  // Retains the last complete dashboard presentation during its shared dwell timer.
  // Sources may change meanwhile; releasing the batch publishes their latest state.
  setDashboardUpdatesDeferred(deferred: boolean): void {
    if (this.dashboardUpdatesDeferred === deferred) return;
    this.dashboardUpdatesDeferred = deferred;
    if (!deferred) {
      this.finishingDashboardScroll = this.pendingScrollRelease;
      this.pendingScrollRelease = false;
      try {
        this.setConnections(this.selectionConnections);
        // Publish visibility only after the final coordinates have been measured.
        if (this.finishingDashboardScroll) this.scrollingProviderIds.value = new Set();
      } finally {
        this.finishingDashboardScroll = false;
      }
    }
  }
  readonly displayedConnections = new WatchableValue<readonly AnalyserConnection[]>([], "connectionAnalyser.displayedConnections");
  /** Explicit analysis evidence only: transient hover lines must never open tree branches. */
  readonly expansionConnections = new WatchableValue<readonly AnalyserConnection[]>([], "connectionAnalyser.expansionConnections");
  /** Explicit dashboard tracking, retaining its origin so it cannot expand back into that column. */
  readonly dashboardExpansionSources = new WatchableValue<ReadonlyMap<string, { columnId: string; connections: readonly AnalyserConnection[] }>>(new Map(), "connectionAnalyser.dashboardExpansionSources");

  getDashboardExpansionConnections(columnId: string): readonly AnalyserConnection[] {
    const connections = new Map<string, AnalyserConnection>();
    for (const source of this.dashboardExpansionSources.value.values()) {
      if (source.columnId === columnId) continue;
      for (const connection of source.connections) if (!connection.isDeleted) connections.set(connection.id, connection);
    }
    return [...connections.values()];
  }

  private hoverConnections: readonly AnalyserConnection[] = [];
  private selectionConnections: readonly Connection[] = [];
  private connectionSources = new Map<string, readonly AnalyserConnection[]>();
  private addedConnections = new Map<string, Connection>();
  private providers = new Map<string, ConnectionEndpointProvider>();
  private availableEndpoints: readonly ConnectableEndpoint[] = [];
  private editMode: string | undefined;

  /** Rebuilds display and expansion evidence immediately when the plan mode changes. */
  setEditMode(mode: string | undefined): void {
    if (this.editMode === mode) return;
    this.editMode = mode;
    this.setConnections(this.selectionConnections);
  }

  /** Returns visible candidates, including objects without any connection. */
  get connectableEndpoints(): readonly ConnectableEndpoint[] {
    // Read the live flag even when geometry is cached (e.g. during an active drag).
    // All surfaces use this list both for creation grabs and target hit testing.
    // DALI defaults to noCable but still needs gateway and control connections.
    return this.availableEndpoints.filter(endpoint =>
      isConnectionEndpointVisibleInMode(endpoint.object, this.editMode)
      && (endpoint.object.className !== "Consumer" || !(endpoint.object as Consumer).noCable
        || (endpoint.object as Consumer).isDali),
    );
  }

  /** Measures candidates once per layout refresh, independently of selected connections. */
  refreshConnectableEndpoints(): void {
    if (this.layoutResizing.value || this.navigationActive.value || (this.scrollingProviderIds.value.size && !this.finishingDashboardScroll)) return;
    const result: ConnectableEndpoint[] = [];
    if (this.visible.value) {
      for (const provider of this.providers.values()) {
        try {
          for (const endpoint of provider.getConnectableEndpoints?.() ?? []) {
            if (endpoint.object.isDeleted || !Number.isFinite(endpoint.point.clientX) || !Number.isFinite(endpoint.point.clientY)) continue;
            result.push({ ...endpoint, key: `${provider.id}:${endpoint.key}`, providerId: provider.id });
          }
        } catch (error) {
          console.warn("Connection candidate provider failed", provider.id, error);
        }
      }
    }
    this.availableEndpoints = result;
  }

  /** Identifies overlay state that must not invalidate the surrounding editor. */
  isPresentationState(object: BaseObject): boolean {
    return (
      object === this.visible ||
      object === this.portalTarget ||
      object === this.manuallyEnabled ||
      object === this.navigationActive ||
      object === this.layoutResizing ||
      object === this.scrollingProviderIds ||
      object === this.hasConnectionSources ||
      object === this.selectedSource ||
      object === this.selectedVirtualConnectionId ||
      object === this.endpoints || object === this.displayedConnections || object === this.expansionConnections || object === this.dashboardExpansionSources
    );
  }

  get connections(): readonly AnalyserConnection[] {
    return this.current;
  }
  /** Freezes connection geometry while dividers move and measures the final layout on release. */
  setLayoutResizing(active: boolean): void {
    if (this.layoutResizing.value === active) return;
    this.layoutResizing.value = active;
    if (!active) this.refreshEndpoints();
  }
  /** Freezes geometry during tree scrolling; the view hides only routes touching these providers. */
  setScrollingProviders(ids: ReadonlySet<string>): void {
    if (!ids.size && this.dashboardUpdatesDeferred) {
      this.pendingScrollRelease = true;
      return;
    }
    this.pendingScrollRelease = false;
    const previous = this.scrollingProviderIds.value;
    if (previous.size === ids.size && [...ids].every(id => previous.has(id))) return;
    this.scrollingProviderIds.value = new Set(ids);
    if (!ids.size) this.refreshEndpoints();
  }
  /** Suspends geometry measurements during navigation and rebuilds anchors when it finishes. */
  setNavigationActive(active: boolean): void {
    if (this.navigationActive.value === active) return;
    this.navigationActive.value = active;
    if (active) {
      this.availableEndpoints = [];
      // Retain the last geometry so the view can transform it without measuring moving controls.
    } else {
      this.refreshEndpoints();
    }
  }
  /** Replaces evidence only for primary objects; secondary navigation preserves it, undefined explicitly clears it. */
  selectObject(object: BaseObject | undefined): void {
    this.selectedVirtualConnectionId.value = undefined;
    if (object && connectionSelectionRole(object) === "secondary") return;
    this.addedConnections.clear();
    this.selectedSource.value = object;
    this.setConnections(collectSelectedConnections(object));
  }
  setConnections(connections: readonly Connection[]): void {
    this.selectionConnections = connections;
    if (this.dashboardUpdatesDeferred) return;
    const unique = new Map<string, AnalyserConnection>();
    if (this.connectionSources.size > 0) {
      for (const source of this.connectionSources.values()) {
        for (const connection of source) if (!connection.isDeleted) unique.set(connection.id, connection);
      }
    } else {
      for (const connection of connections) if (!unique.has(connection.id)) unique.set(connection.id, connection);
      for (const connection of this.addedConnections.values()) unique.set(connection.id, connection);
    }
    const expansion = [...unique.values()].filter(connection => isConnectionVisibleInMode(connection, this.editMode));
    if (expansion.length !== this.expansionConnections.value.length || expansion.some((connection, index) => connection !== this.expansionConnections.value[index])) {
      this.expansionConnections.value = expansion;
    }
    for (const connection of this.hoverConnections) {
      if (!connection.isDeleted && !unique.has(connection.id)) unique.set(connection.id, connection);
    }
    this.current = [...unique.values()].filter(connection => isConnectionVisibleInMode(connection, this.editMode));
    if (this.current.length !== this.displayedConnections.value.length || this.current.some((connection, index) => connection !== this.displayedConnections.value[index])) {
      this.displayedConnections.value = this.current;
    }
    this.refreshEndpoints();
  }
  /** Adds a reversible hover preview without changing selection, sources or expansion evidence. */
  setHoverConnections(connections: readonly AnalyserConnection[]): void {
    if (connections.length === this.hoverConnections.length && connections.every((connection, index) => connection === this.hoverConnections[index])) return;
    this.hoverConnections = connections;
    this.setConnections(this.selectionConnections);
  }
  /** Lets mounted browsers own the displayed evidence independently of ordinary selection. */
  setConnectionSource(id: string, connections: readonly AnalyserConnection[], expansionColumnId?: string): void {
    this.connectionSources.set(id, connections);
    const previous = this.dashboardExpansionSources.value.get(id);
    if (expansionColumnId && connections.length) {
      if (previous?.columnId !== expansionColumnId || previous.connections.length !== connections.length
        || connections.some((connection, index) => connection !== previous.connections[index])) {
        const sources = new Map(this.dashboardExpansionSources.value);
        sources.set(id, { columnId: expansionColumnId, connections });
        this.dashboardExpansionSources.value = sources;
      }
    } else if (previous) {
      const sources = new Map(this.dashboardExpansionSources.value);
      sources.delete(id);
      this.dashboardExpansionSources.value = sources;
    }
    this.hasConnectionSources.value = true;
    this.setConnections(this.selectionConnections);
  }
  /** Restores selection-driven analysis after the last browser source is removed. */
  removeConnectionSource(id: string): void {
    this.connectionSources.delete(id);
    if (this.dashboardExpansionSources.value.has(id)) {
      const sources = new Map(this.dashboardExpansionSources.value);
      sources.delete(id);
      this.dashboardExpansionSources.value = sources;
    }
    this.hasConnectionSources.value = this.connectionSources.size > 0;
    this.setConnections(this.selectionConnections);
  }
  /** Keeps newly drawn connections alongside the selected device until the next primary selection. */
  addConnection(connection: Connection): void {
    this.addedConnections.set(connection.id, connection);
    this.setConnections(this.selectionConnections);
  }
  clearConnections(): void {
    this.hoverConnections = [];
    this.addedConnections.clear();
    this.setConnections([]);
  }
  setViewVisible(visible: boolean): void {
    this.visible.value = visible;
    this.refreshEndpoints();
  }
  registerEndpointProvider(provider: ConnectionEndpointProvider): void {
    if (this.providers.has(provider.id)) {
      console.warn("Duplicate Connection Analyser provider", provider.id);
      return;
    }
    this.providers.set(provider.id, provider);
    this.refreshEndpoints();
  }
  unregisterEndpointProvider(provider: ConnectionEndpointProvider): void {
    if (this.providers.get(provider.id) === provider) this.providers.delete(provider.id);
    this.refreshEndpoints();
  }
  refreshEndpoints(): void {
    if (this.dashboardUpdatesDeferred) return;
    if (this.layoutResizing.value || this.navigationActive.value || (this.scrollingProviderIds.value.size && !this.finishingDashboardScroll)) return;
    this.refreshConnectableEndpoints();
    const endpoints: ConnectionEndpoint[] = [];
    if (this.visible.value) {
      for (const connection of this.current) {
        if (
          connection.isDeleted ||
          !connection.fromDevice ||
          !connection.toDevice ||
          connection.fromDevice.isDeleted ||
          connection.toDevice.isDeleted
        )
          continue;
        for (const provider of this.providers.values()) {
          try {
            let index = 0;
            const provided = isVirtualConnection(connection)
              ? provider.getVirtualConnectionEndpoint?.(connection) ?? []
              : provider.getConnectionEndpoint(connection);
            for (const endpoint of provided) {
              if (
                endpoint.connectionId === connection.id &&
                (endpoint.role === "start" || endpoint.role === "end") &&
                Number.isFinite(endpoint.point?.clientX) &&
                Number.isFinite(endpoint.point?.clientY)
              ) {
                endpoints.push({
                  ...endpoint,
                  point: { ...endpoint.point },
                  contour: endpoint.contour ? { ...endpoint.contour } : undefined,
                  exitDirection: endpoint.exitDirection ? { ...endpoint.exitDirection } : undefined,
                  providerId: provider.id,
                  key: `${provider.id}:${connection.id}:${endpoint.role}:${endpoint.key ?? index}`,
                });
              }
              index++;
            }
          } catch (error) {
            console.warn("Connection Analyser provider failed", provider.id, error);
          }
        }
      }
    }
    if (!connectionEndpointsEqual(this.endpoints.value, endpoints)) {
      this.endpoints.value = endpoints;
    }
  }

  /** Reveals real targets through the clicked route's providers without changing the analysis source. */
  bringEndpointsIntoView(endpoints: readonly ConnectionEndpoint[], centerDrawing = false): void {
    const visited = new Set<string>();
    for (const endpoint of endpoints) {
      if (!endpoint.providerId || !endpoint.anchorObjectId) continue;
      const key = JSON.stringify([endpoint.providerId, endpoint.targetObjectId ?? endpoint.anchorObjectId]);
      if (visited.has(key)) continue;
      visited.add(key);
      const provider = this.providers.get(endpoint.providerId);
      if (!provider?.bringIntoView) continue;
      try {
        const center = centerDrawing && endpoint.surface === "drawing" && endpoint.representedObjectClass === "Consumer" && endpoints.some(
          (partner) => partner.connectionId === endpoint.connectionId && partner.role !== endpoint.role && partner.surface !== "drawing",
        );
        const pending = provider.bringIntoView(endpoint.anchorObjectId, endpoint.targetObjectId, { center });
        if (pending) {
          void pending.then(() => this.refreshEndpoints()).catch((error) => {
            console.warn("Connection Analyser could not reveal target", provider.id, error);
          });
        }
      } catch (error) {
        console.warn("Connection Analyser could not reveal target", provider.id, error);
      }
    }
    this.refreshEndpoints();
  }
}

/** Compares the ordered anchors, including geometry and route interaction metadata. */
function connectionEndpointsEqual(previous: readonly ConnectionEndpoint[], next: readonly ConnectionEndpoint[]): boolean {
  if (previous.length !== next.length) return false;
  for (let index = 0; index < previous.length; index++) {
    if (!connectionEndpointEqual(previous[index], next[index])) return false;
  }
  return true;
}

/** Detects changes that affect a line's position, routing, or selectable target. */
function connectionEndpointEqual(previous: ConnectionEndpoint, next: ConnectionEndpoint): boolean {
  return (
    previous.connectionId === next.connectionId &&
    previous.role === next.role &&
    previous.surface === next.surface &&
    previous.isScrollClipped === next.isScrollClipped &&
    previous.isStickyScrollAnchor === next.isStickyScrollAnchor &&
    JSON.stringify(previous.scrollGeometry) === JSON.stringify(next.scrollGeometry) &&
    previous.representedObjectClass === next.representedObjectClass &&
    previous.point.clientX === next.point.clientX &&
    previous.point.clientY === next.point.clientY &&
    endpointContoursEqual(previous.contour, next.contour) &&
    previous.exitDirection?.x === next.exitDirection?.x &&
    previous.exitDirection?.y === next.exitDirection?.y &&
    previous.key === next.key &&
    previous.anchorObjectId === next.anchorObjectId &&
    previous.hoverElement === next.hoverElement &&
    previous.targetObjectId === next.targetObjectId &&
    previous.providerId === next.providerId
  );
}
